import { Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { formatXpKSuffix, formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import { creatorProfilePath } from "@/routes"
import type { CurrentBrand } from "@/types"

// OKS-48 — Narrow shared-props slice this pill consumes. Inlining the
// shape (vs. `Partial<SharedProps>`) keeps eslint's `react/prop-types`
// rule happy — a deep-Partial of the canonical SharedProps trips the
// rule since every member is `unknown`-ish; an explicit shape lets it
// resolve. Mirrors the OKS-36 `BellTrigger` shared-prop pattern.
interface XpPillSharedSlice {
  current_brand_xp_balance?: number
  currentBrand: CurrentBrand | null
  // Index signature satisfies the `usePage<T extends PageProps>`
  // constraint — Inertia ships `Page<T>` whose `props` is widened
  // beyond the named keys we actually consume.
  [key: string]: unknown
}

/**
 * OKS-48 — Creator-shell header pill.
 *
 * Lives in `creator-shell.tsx` next to the brand switcher. Shows the
 * creator's per-brand XP balance for `Current.brand` (BE shared prop
 * `current_brand_xp_balance` — lambda form, partial-reload friendly).
 *
 * # States (UX Spec §3)
 *
 *   - Empty (`0 XP`)         → render the literal "0", same caption colour
 *                              as a non-zero balance (UX Spec §6 a11y —
 *                              colour is not the sole signal).
 *   - Loading                → `<Skeleton>` sized to the eventual width.
 *                              Triggered when the shared prop is `undefined`
 *                              (creator without a current membership) or
 *                              during a partial reload that strips the prop.
 *   - Error                  → pill hides cleanly (no broken UI) per UX
 *                              Spec §3 — implemented via a `null` return
 *                              when the shared prop is missing AND we know
 *                              we're on a creator shell.
 *   - Success                → `1,247 XP` with locale-aware grouping.
 *   - Edge (mobile <320px)   → `1.2K XP` collapsed via `formatXpKSuffix`.
 *                              Tailwind `@container` query responds to
 *                              parent header width; we use a class-based
 *                              switch with a `sr-only` full-number for
 *                              assistive tech.
 *
 * # Accessibility
 *
 *   - `<Link>` from Inertia → focusable, semantic, keyboard-navigable
 *   - `aria-label` reads the FULL number even on the K-collapse variant
 *     so screen readers never get the lossy "1.2K" representation
 *   - Tap target ≥44×44 px via padding on the wrapper
 *   - `aria-busy="true"` on the Skeleton parent during loading
 */
export function XpPill() {
  const { t, i18n } = useTranslation()
  // Rename `props` to `pageProps` so react/prop-types doesn't conflate
  // shared-prop access with component prop validation (the rule treats
  // a local named `props` as component-prop access). Same OKS-36
  // BellTrigger workaround.
  const pageProps = usePage<XpPillSharedSlice>().props
  const balance = pageProps.current_brand_xp_balance
  const brandName = pageProps.currentBrand?.name ?? ""

  // Loading branch — the shared prop is `undefined` while a partial
  // reload that selects only this prop is in flight, OR before
  // `Current.membership` is resolved on a fresh creator session.
  if (balance === undefined) {
    return (
      <div
        aria-busy="true"
        aria-label={t("xp.loading")}
        className="flex h-11 w-16 min-w-11 items-center justify-center"
        data-testid="xp-pill-loading"
      >
        <Skeleton className="h-5 w-12 rounded-full" />
      </div>
    )
  }

  const fullNumber = formatXpNumber(balance, i18n.language)
  const collapsedNumber = formatXpKSuffix(balance, i18n.language)
  const xpSuffix = t("xp.suffix")
  const ariaLabel = t("xp.aria_label", { count: fullNumber, brand: brandName })

  return (
    <Link
      href={creatorProfilePath()}
      aria-label={ariaLabel}
      data-testid="xp-pill"
      className={cn(
        // Tap target padding — outer anchor pads to ≥44×44 around the
        // smaller visual badge (UX Spec §5).
        "inline-flex h-11 min-w-11 items-center justify-center px-2",
        "focus-visible:ring-ring rounded-md focus-visible:ring-2 focus-visible:outline-none",
      )}
    >
      <Badge variant="secondary" data-testid="xp-pill-badge">
        {/* Collapsed variant — visible on tight viewports.
            `sr-only` full-number sibling keeps the SR announcement
            faithful to the exact value (UX Spec §6 a11y). */}
        {/* OKS-646 — re-add the visible "XP" suffix on the mobile branch
            that OKS-387 had dropped (it left a bare number that new
            creators couldn't read as XP). The suffix renders at a smaller
            caption weight so it fits the 320px header — OKS-387 freed the
            space by dropping the logo + folding Messages/Bell/Activity into
            "More" — WITHOUT re-breaking that horizontal-overflow fix. This
            caption-weight treatment is the deterministic default (no JS /
            ResizeObserver measurement). K/M collapse (`collapsedNumber`) is
            retained; the `sr-only` sibling + `aria-label` still carry the
            faithful full-value "N XP" for assistive tech (UX Spec §6). */}
        <span
          aria-hidden="true"
          className="sm:hidden"
          data-testid="xp-pill-value-mobile"
        >
          {collapsedNumber}{" "}
          <span className="text-[0.625rem] font-medium">{xpSuffix}</span>
        </span>
        <span aria-hidden="true" className="hidden sm:inline">
          {fullNumber} {xpSuffix}
        </span>
        <span className="sr-only">
          {fullNumber} {xpSuffix}
        </span>
      </Badge>
    </Link>
  )
}
